import React, { useState } from 'react';

const FinancialHealthCheckup = ({ config }) => {
  const [age, setAge] = useState(config.age || 49);
  const [monthlyIncome, setMonthlyIncome] = useState(config.monthlyIncome || 200000);
  const [emiAmount, setEmiAmount] = useState(config.emiAmount || 25000);
  const [monthlyInvestment, setMonthlyInvestment] = useState(config.monthlyInvestment || 50000);

  const labelMappings = {
    investmentAssets: {
      liquid: 'Liquid',
      debt: 'Debt',
      equity: 'Equity',
      commodity: 'Commodity',
      reit: 'REIT / Structured Products',
      alternative: 'Alternative Investment',
    },
    liabilities: {
      homeLoan: 'Home Loan',
      vehicleLoan: 'Vehicle Loan',
      personalLoan: 'Personal Loan',
      educationLoan: 'Education Loan',
      consumerLoan: 'Consumer Loan',
      creditCard: 'Credit Card',
    },
    physicalAssets: {
      property: 'Property (excl. non-primary)',
      gold: 'Gold, Silver (inv. purpose)',
      primaryResidence: 'Primary Residence',
      personalVehicles: 'Personal Vehicles',
      jewellery: 'Jewellery',
    },
  };

  const [investmentAssets, setInvestmentAssets] = useState(config.investmentAssets || {
    liquid: 1000000,
    debt: 2000000,
    equity: 3000000,
    commodity: 0,
    reit: 0,
    alternative: 0,
  });

  const [liabilities, setLiabilities] = useState(config.liabilities || {
    homeLoan: 2500000,
    vehicleLoan: 0,
    personalLoan: 0,
    educationLoan: 0,
    consumerLoan: 0,
    creditCard: 0,
  });

  const [physicalAssets, setPhysicalAssets] = useState(config.physicalAssets || {
    property: 5000000,
    gold: 0,
    primaryResidence: 17500000,
    personalVehicles: 1500000,
    jewellery: 1000000,
  });

  const handleInputChange = (setter) => (e) => {
    setter(parseFloat(e.target.value));
  };

  const calculateNetWorth = () => {
    const investmentTotal = Object.values(investmentAssets).reduce((a, b) => a + b, 0);
    const liabilityTotal = Object.values(liabilities).reduce((a, b) => a + b, 0);
    const physicalTotal = Object.values(physicalAssets).reduce((a, b) => a + b, 0);
    const netWorthIncludingPersonal = investmentTotal + physicalTotal - liabilityTotal;
    const netWorthExcludingPersonal = investmentTotal + physicalAssets.property + physicalAssets.gold - liabilityTotal;
    return {
      netWorthIncludingPersonal,
      netWorthExcludingPersonal,
      minimumRequiredNetWorth: age * (monthlyIncome * 12) / 10,
    };
  };

  const calculateDebtToIncomeRatio = () => {
    const debtToIncomeRatio = (emiAmount / monthlyIncome) * 100;
    return {
      emiAmount,
      debtToIncomeRatio,
      maximumAllowedRatio: 30,
    };
  };

  const calculateInvestmentRatio = () => {
    const investmentRatio = (monthlyInvestment / monthlyIncome) * 100;
    return {
      monthlyInvestment,
      investmentRatio,
      minimumRequiredRatio: 35,
    };
  };

  const calculateFinancialAssetsRatio = () => {
    const totalFinancialAssets = Object.values(investmentAssets).reduce((a, b) => a + b, 0);
    const totalAssets = totalFinancialAssets + Object.values(physicalAssets).reduce((a, b) => a + b, 0);
    const financialAssetsRatio = (totalFinancialAssets / totalAssets) * 100;
    return {
      totalFinancialAssets,
      financialAssetsRatio,
      minimumRequiredRatio: 20,
    };
  };

  const netWorth = calculateNetWorth();
  const debtToIncome = calculateDebtToIncomeRatio();
  const investment = calculateInvestmentRatio();
  const financialAssets = calculateFinancialAssetsRatio();

  return (
    <div className="financial-health-checkup">
      <h4>{config.title}</h4>
      <div className="row">
          <div className="col-md-6">
          <table className="input-section">
        <tbody>
          <tr>
            <td className='w-60'><label>Your age:</label></td>
            <td className='ls-0 '><input type="number" value={age} onChange={handleInputChange(setAge)} /></td>
          </tr>
          <tr>
            <td className='w-60 table-alt-bg'><label>Current EMI amount you're paying monthly:</label></td>
            <td className='ls-0'><input type="number" value={emiAmount} onChange={handleInputChange(setEmiAmount)} /></td>
          </tr>
        </tbody>
      </table>

          </div>
          <div className="col-md-6">
          <table className="input-section">
        <tbody>
          <tr>
            <td className='w-60'><label>Your monthly income:</label></td>
            <td className='ls-0'><input type="number" value={monthlyIncome} onChange={handleInputChange(setMonthlyIncome)} /></td>
          </tr>
          <tr>
            <td className='w-60 table-alt-bg'><label>Your current monthly investment amount:</label></td>
            <td className='ls-0'><input type="number" value={monthlyInvestment} onChange={handleInputChange(setMonthlyInvestment)} /></td>
          </tr>
        </tbody>
      </table>

          </div>
        </div>
      

      <div className="assets-liabilities-section">
        <h6 className='head-main'>Your assets & liabilities details:</h6>
        <div className="row">
          <div className="col-md-4">
            <h6 className='bg-blue text-center head'>Investment Assets</h6>
            <table className='table-container'>
              <tbody>
                {Object.keys(investmentAssets).map((key) => (
                  <tr key={key}>
                    <td className="w-60"><label>{labelMappings.investmentAssets[key]}:</label></td>
                    {/* <td className='w-60'><label>{key.charAt(0).toUpperCase() + key.slice(1)}:</label></td> */}
                    <td className='ls-0'>
                      <input
                        type="number"
                        value={investmentAssets[key]}
                        onChange={(e) => setInvestmentAssets({ ...investmentAssets, [key]: parseFloat(e.target.value) })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="col-md-4">
            <h6 className='bg-blue text-center head'>Liabilities</h6>
            <table className='table-container'>
              <tbody>
                {Object.keys(liabilities).map((key) => (
                  <tr key={key}>
                    <td className="w-60"><label>{labelMappings.liabilities[key]}:</label></td>
                    {/* <td className='w-60'><label>{key.charAt(0).toUpperCase() + key.slice(1)}:</label></td> */}
                    <td className='ls-0'>
                      <input
                        type="number"
                        value={liabilities[key]}
                        onChange={(e) => setLiabilities({ ...liabilities, [key]: parseFloat(e.target.value) })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="col-md-4">
            <h6 className='bg-blue text-center head'>Physical Assets</h6>
            <table className='table-container'>
              <tbody>
                {Object.keys(physicalAssets).map((key) => (
                  <tr key={key}>
                    <td className="w-60"><label>{labelMappings.physicalAssets[key]}:</label></td>
                    {/* <td className='w-60'><label>{key.charAt(0).toUpperCase() + key.slice(1)}:</label></td> */}
                    <td className='ls-0'>
                      <input
                        type="number"
                        value={physicalAssets[key]}
                        onChange={(e) => setPhysicalAssets({ ...physicalAssets, [key]: parseFloat(e.target.value) })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      <div className="summary-tables">
        <div className="summary-table">
          <h5 className='head'>Net Worth</h5>
          <table className='table-container'>
            <tbody>
              <tr>
                <td>Net worth including personal assets (A)</td>
                <td className='align-right'>{netWorth.netWorthIncludingPersonal.toLocaleString()}</td>
              </tr>
              <tr>
                <td className='table-alt-bg'>Net worth excluding personal assets (B)</td>
                <td className={`table-alt-bg align-right ${netWorth.netWorthExcludingPersonal < netWorth.minimumRequiredNetWorth ? 'bg-red' : ''}`}>
                  {netWorth.netWorthExcludingPersonal.toLocaleString()}</td>
              </tr>
              <tr>
                <td className='bg-blue'>Above net worth (B) shouldn't be less than</td>
                <td className='bg-blue align-right'>{netWorth.minimumRequiredNetWorth.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="summary-table">
          <h5 className='head'>Debt to Income Ratio</h5>
          <table className='table-container'>
            <tbody>
              <tr>
                <td>Your current EMI amount</td>
                <td className='align-right'>{debtToIncome.emiAmount.toLocaleString()}</td>
              </tr>
              <tr>
                <td className='table-alt-bg'>Your debt to income ratio</td>
                <td className={`table-alt-bg align-right ${debtToIncome.debtToIncomeRatio > debtToIncome.maximumAllowedRatio ? 'bg-red' : ''}`}>
                  {debtToIncome.debtToIncomeRatio.toFixed(2)}%</td>
              </tr>
              <tr>
                <td className='bg-blue'>This shouldn't be more than</td>
                <td className='bg-blue align-right'>{debtToIncome.maximumAllowedRatio.toFixed(2)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="summary-table">
          <h5 className='head'>Investment Ratio</h5>
          <table className='table-container'>
            <tbody>
              <tr>
                <td>Your current monthly investment</td>
                <td className='align-right'>{investment.monthlyInvestment.toLocaleString()}</td>
              </tr>
              <tr>
                <td className='table-alt-bg'>Your current investment ratio</td>
                <td className={`table-alt-bg align-right ${investment.investmentRatio < investment.minimumRequiredRatio ? 'bg-red' : ''}`}>
                  {investment.investmentRatio.toFixed(2)}%</td>
              </tr>
              <tr>
                <td className='bg-blue'>This shouldn't be less than</td>
                <td className='bg-blue align-right'>{investment.minimumRequiredRatio.toFixed(2)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="summary-table">
          <h5 className='head'>Financial Assets Ratio</h5>
          <table className='table-container'>
            <tbody>
              <tr>
                <td>Total financial assets (liquid, debt, equity, etc.)</td>
                <td className='align-right'>{financialAssets.totalFinancialAssets.toLocaleString()}</td>
              </tr>
              <tr>
                <td className='table-alt-bg'>Your financial assets ratio</td>
                <td className={`align-right ${financialAssets.financialAssetsRatio < financialAssets.minimumRequiredRatio ? 'bg-red' : ''} table-alt-bg`}>
                  {financialAssets.financialAssetsRatio.toFixed(2)}%</td>
              </tr>
              <tr>
                <td className='bg-blue'>This shouldn't be less than</td>
                <td className='bg-blue align-right'>{financialAssets.minimumRequiredRatio.toFixed(2)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FinancialHealthCheckup;
